/**
 * Answer input widget factory. Renders the right input for a question's
 * answerType: multiple-choice buttons for "choice" questions, a text field for
 * everything else. Returns a small controller the screens use to read the
 * current raw answer, focus, and reset between questions.
 */

import { el } from "./dom";
import { mathElement } from "./math-display";
import { plotElement } from "../graph/svg-render";
import type { Question } from "../topics/types";

export interface AnswerInput {
  readonly element: HTMLElement;
  /** Current raw answer string ("" if none selected/entered). */
  getValue(): string;
  focus(): void;
  reset(): void;
  /** Enable/disable interaction (e.g. after a correct answer in practice). */
  setEnabled(enabled: boolean): void;
  /** For choice inputs: highlight the chosen and (optionally) correct option. */
  markChoice?(chosenId: string, correctId: string): void;
}

export interface AnswerInputOptions {
  /** Called when the student commits (Enter in text, or clicking a choice). */
  onSubmit?: () => void;
  placeholder?: string;
}

export function createAnswerInput(
  question: Question,
  opts: AnswerInputOptions = {},
): AnswerInput {
  if (question.answerType === "choice" && question.choices) {
    return createChoiceInput(question, opts);
  }
  return createTextInput(question, opts);
}

/**
 * Symbols the on-screen palette offers, chosen per answer type. These insert
 * the exact characters the answer parser already accepts (the parser
 * normalizes <= / >= and unicode variants), so a student never has to hunt for
 * a slash or an inequality sign on a tablet keyboard.
 */
function paletteKeys(answerType: Question["answerType"]): string[] {
  switch (answerType) {
    case "number":
    case "set":
      return ["/", "-", ",", "(", ")"];
    case "inequality":
      return ["<", ">", "\u2264", "\u2265", "-", "/", "x"];
    case "compound":
      return ["<", ">", "\u2264", "\u2265", "-", "or", "x"];
    default:
      // boolean / special: free-text words, no symbol palette needed.
      return [];
  }
}

function createTextInput(
  question: Question,
  opts: AnswerInputOptions,
): AnswerInput {
  const input = el("input", {
    class: "answer-input",
    type: "text",
    autocomplete: "off",
    autocapitalize: "off",
    spellcheck: "false",
    "aria-label": "Your answer",
    placeholder: opts.placeholder ?? "your answer",
  }) as HTMLInputElement;

  input.addEventListener("keydown", (e) => {
    if ((e as KeyboardEvent).key === "Enter") opts.onSubmit?.();
  });

  /** Insert text at the caret (or replace the selection), then refocus. */
  const insert = (token: string): void => {
    if (input.disabled) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    // A word token like "or" reads better with surrounding spaces.
    const chunk = /^[a-z]+$/.test(token) ? ` ${token} ` : token;
    input.value = input.value.slice(0, start) + chunk + input.value.slice(end);
    const caret = start + chunk.length;
    input.focus();
    input.setSelectionRange(caret, caret);
  };

  const wrap = el("div", { class: "answer-row" }, input);

  const keys = paletteKeys(question.answerType);
  if (keys.length > 0) {
    const palette = el("div", {
      class: "answer-palette",
      "aria-label": "Symbol palette",
    });
    for (const key of keys) {
      const b = el(
        "button",
        {
          class: "palette-key",
          type: "button",
          tabindex: -1,
          "aria-label": `Insert ${key}`,
          onClick: () => insert(key),
        },
        key,
      );
      palette.appendChild(b);
    }
    wrap.appendChild(palette);
  }

  return {
    element: wrap,
    getValue: () => input.value.trim(),
    focus: () => input.focus(),
    reset: () => {
      input.value = "";
      input.disabled = false;
    },
    setEnabled: (enabled) => {
      input.disabled = !enabled;
    },
  };
}

function createChoiceInput(
  question: Question,
  opts: AnswerInputOptions,
): AnswerInput {
  let selectedId = "";
  const buttons = new Map<string, HTMLButtonElement>();

  const list = el("div", {
    class: "options",
    role: "radiogroup",
    "aria-label": "Answer choices",
    style: "flex-direction:column;align-items:stretch",
  });

  for (const choice of question.choices!) {
    const btn = el("button", {
      class: "chip",
      role: "radio",
      "aria-checked": "false",
      "data-choice": choice.id,
      style: "text-align:left;display:flex;align-items:center;gap:0.5rem",
    }) as HTMLButtonElement;

    if (choice.graph) btn.appendChild(plotElement(choice.graph));
    else if (choice.latex) btn.appendChild(mathElement(choice.latex, false));
    else if (choice.label) btn.appendChild(document.createTextNode(choice.label));

    btn.addEventListener("click", () => {
      selectedId = choice.id;
      for (const [id, b] of buttons) {
        b.setAttribute("aria-checked", id === selectedId ? "true" : "false");
        b.setAttribute("aria-pressed", id === selectedId ? "true" : "false");
      }
      opts.onSubmit?.();
    });

    buttons.set(choice.id, btn);
    list.appendChild(btn);
  }

  return {
    element: list,
    // The choice grading compares against the "correct" id; the recorded
    // answer is a ChoiceAnswer with value "correct", so we return the selected
    // id and let the caller compare. For screens that grade via gradeAnswer we
    // instead expose the selected id directly.
    getValue: () => selectedId,
    focus: () => {
      const first = buttons.values().next().value;
      first?.focus();
    },
    reset: () => {
      selectedId = "";
      for (const b of buttons.values()) {
        b.setAttribute("aria-checked", "false");
        b.removeAttribute("aria-pressed");
        b.disabled = false;
        b.classList.remove("chip-correct", "chip-incorrect");
      }
    },
    setEnabled: (enabled) => {
      for (const b of buttons.values()) b.disabled = !enabled;
    },
    markChoice: (chosenId, correctId) => {
      for (const [id, b] of buttons) {
        if (id === correctId) b.classList.add("chip-correct");
        else if (id === chosenId) b.classList.add("chip-incorrect");
      }
    },
  };
}
